import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faSync } from "@fortawesome/free-solid-svg-icons"
import { fetchVehModeles } from "../../../store/actions/autreActions"
import {
	postClientVehicule,
	deleteClientVehicule,
} from "../../../store/actions/entiteActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import CustomModal from "../../basics/interface/CustomModal"
import FilteredSelectV2 from "../../basics/input/FilteredSelectV2"
import VehiculeDocumentsV2 from "../entite/VehiculeDocumentsV2"

const togglerStyle = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	cursor: "pointer",
	padding: "10px 2px",
	fontSize: 13,
	fontWeight: 500,
	color: COLORS.textMuted,
	userSelect: "none",
	borderTop: `1px solid ${COLORS.border}`,
	marginTop: 6,
}

const styles = {
	// Tableau dans une carte : padding réduit, en-têtes sans fond `bg`.
	th: {
		...tableStyles.th,
		padding: "10px 14px",
		backgroundColor: "transparent",
	},
	td: {
		...tableStyles.td,
		padding: "10px 14px",
	},
	empty: { color: COLORS.textLight, fontSize: 13, padding: "8px 2px" },
	notice: { color: COLORS.textLight, fontSize: 13, padding: "8px 2px" },
}

// Champs communs aux véhicules d'un client — SANS propre_vehicule ni ses
// options. Save/suppression immédiats via l'endpoint dédié.
const VehiculeModal = ({
	vehicule,
	entiteId,
	onClose,
	onSaved,
	onDocsChange,
}) => {
	const dispatch = useDispatch()
	const marques = useSelector((s) => s.autreInfo.marques)
	const modeles = useSelector((s) => s.autreInfo.modeles)
	const isNew = !vehicule.id

	const [form, setForm] = useState(() => ({ ...vehicule }))
	const [detailsOpen, setDetailsOpen] = useState(false)
	const [docsOpen, setDocsOpen] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const detailsRef = useRef(null)
	const docsRef = useRef(null)
	const [docsH, setDocsH] = useState(0)
	// Mesure dynamique de la hauteur de la section Documents (contenu
	// variable : on observe le contenu interne, non clippé).
	useLayoutEffect(() => {
		const el = docsRef.current
		if (!el) return
		const measure = () => setDocsH(docsOpen ? el.scrollHeight : 0)
		measure()
		if (typeof ResizeObserver === "undefined") return
		const ro = new ResizeObserver(measure)
		ro.observe(el)
		return () => ro.disconnect()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [docsOpen])
	const refMarque = useRef(null)
	const refModele = useRef(null)
	const refPlaques = useRef(null)
	const refChassis = useRef(null)
	const refDateMEC = useRef(null)
	const refExpertise = useRef(null)
	const refKms = useRef(null)
	const update = (k, v) => setForm((f) => ({ ...f, [k]: v }))

	// Entrée → champ suivant.
	const focusNext = (e, ref) => {
		if (e.key === "Enter") {
			e.preventDefault()
			if (ref.current) ref.current.focus()
		}
	}
	// Sur Plaques : Entrée ouvre les détails et y entre.
	const plaquesEnter = (e) => {
		if (e.key === "Enter") {
			e.preventDefault()
			setDetailsOpen(true)
			setTimeout(() => refChassis.current && refChassis.current.focus(), 0)
		}
	}

	useEffect(() => {
		if (form.marque) {
			const m = marques.find((x) => x.nom === form.marque)
			if (m) dispatch(fetchVehModeles(m.id))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const onSelectMarque = (option) => {
		setForm((f) => ({ ...f, marque: option.nom, modele: "" }))
		dispatch(fetchVehModeles(option.id))
	}

	const busy = saving || deleting

	const save = () => {
		setSaving(true)
		dispatch(
			postClientVehicule(entiteId, {
				id: vehicule.id,
				plaques: form.plaques || "",
				marque: form.marque || "",
				modele: form.modele || "",
				chassis: form.chassis || "",
				date_mise_en_circu: form.date_mise_en_circu || "",
				expertise_le: form.expertise_le || "",
				kms: form.kms === "" || form.kms == null ? 0 : form.kms,
			}),
		).then((client) => {
			setSaving(false)
			if (client) onSaved(client)
		})
	}

	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		setDeleting(true)
		dispatch(deleteClientVehicule(entiteId, vehicule.id)).then((client) => {
			setDeleting(false)
			if (client) onSaved(client)
		})
	}

	const invalid = !(form.marque || "").trim() || !(form.modele || "").trim()

	const buttons = []
	if (!isNew) {
		buttons.push({
			title: deleting ? (
				<FontAwesomeIcon icon={faSync} spin />
			) : confirmDelete ? (
				"Confirmer ?"
			) : (
				"Supprimer"
			),
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: saving ? <FontAwesomeIcon icon={faSync} spin /> : "Enregistrer",
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	return (
		<CustomModal
			title={isNew ? "Nouveau véhicule" : "Véhicule"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Marque<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<FilteredSelectV2
						options={marques}
						defaultKey="nom"
						optionKeys="nom"
						value={form.marque || ""}
						placeholder="Marque…"
						inputRef={refMarque}
						nextRef={refModele}
						onChange={(v) => update("marque", v)}
						onSelect={onSelectMarque}
					/>
				</div>

				{form.marque ? (
					<div style={gs.fieldRow}>
						<label style={gs.label}>
							Modèle<span style={{ color: COLORS.danger }}> *</span>
						</label>
						<FilteredSelectV2
							options={modeles}
							defaultKey="nom"
							optionKeys="nom"
							value={form.modele || ""}
							placeholder="Modèle…"
							inputRef={refModele}
							nextRef={refPlaques}
							onChange={(v) => update("modele", v)}
							onSelect={(o) => update("modele", o.nom)}
						/>
					</div>
				) : null}

				<div style={gs.fieldRow}>
					<label style={gs.label}>Plaques</label>
					<input
						ref={refPlaques}
						style={gs.input}
						value={form.plaques || ""}
						onChange={(e) => update("plaques", e.target.value)}
						onKeyDown={plaquesEnter}
					/>
				</div>

				<div
					style={togglerStyle}
					onClick={() => setDetailsOpen((o) => !o)}
				>
					<span>Détails techniques</span>
					<span
						style={{
							display: "inline-block",
							transform: detailsOpen ? "rotate(90deg)" : "none",
							transition: "transform 0.15s",
						}}
					>
						▸
					</span>
				</div>

				<div
					style={{
						overflow: "hidden",
						maxHeight: detailsOpen
							? detailsRef.current
								? detailsRef.current.scrollHeight
								: 400
							: 0,
						opacity: detailsOpen ? 1 : 0,
						transition: "max-height 0.28s ease, opacity 0.2s ease",
					}}
				>
					<div ref={detailsRef} style={{ ...gs.fieldGroup, paddingTop: 4 }}>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Châssis</label>
							<input
								ref={refChassis}
								style={gs.input}
								value={form.chassis || ""}
								onChange={(e) =>
									update("chassis", e.target.value)
								}
								onKeyDown={(e) => focusNext(e, refDateMEC)}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Mise en circulation</label>
							<input
								ref={refDateMEC}
								type="date"
								style={gs.input}
								value={form.date_mise_en_circu || ""}
								onChange={(e) =>
									update("date_mise_en_circu", e.target.value)
								}
								onKeyDown={(e) => focusNext(e, refExpertise)}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Expertise</label>
							<input
								ref={refExpertise}
								type="date"
								style={gs.input}
								value={form.expertise_le || ""}
								onChange={(e) =>
									update("expertise_le", e.target.value)
								}
								onKeyDown={(e) => focusNext(e, refKms)}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Kilométrage</label>
							<input
								ref={refKms}
								type="number"
								style={gs.input}
								value={form.kms ?? ""}
								onChange={(e) =>
									update(
										"kms",
										e.target.value === ""
											? ""
											: parseInt(e.target.value),
									)
								}
							/>
						</div>
					</div>
				</div>
				<div
					style={togglerStyle}
					onClick={() => setDocsOpen((o) => !o)}
				>
					<span>Documents</span>
					<span
						style={{
							display: "inline-block",
							transform: docsOpen ? "rotate(90deg)" : "none",
							transition: "transform 0.15s",
						}}
					>
						▸
					</span>
				</div>
				<div
					style={{
						overflow: "hidden",
						maxHeight: docsH,
						opacity: docsOpen ? 1 : 0,
						transition: "max-height 0.28s ease, opacity 0.2s ease",
					}}
				>
					<div ref={docsRef} style={{ paddingTop: 4 }}>
						<VehiculeDocumentsV2
							vehiculeId={vehicule.id}
							initialFiles={vehicule.files}
							initialCarteGrise={vehicule.carte_grise}
							isNew={isNew}
							onDocsChange={onDocsChange}
						/>
					</div>
				</div>
			</div>
		</CustomModal>
	)
}

const VehiculeRow = ({ veh, index, onOpen }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={onOpen}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			<td style={{ ...styles.td, fontWeight: 500 }}>
				{veh.plaques || "—"}
			</td>
			<td style={styles.td}>{veh.marque || "—"}</td>
			<td style={styles.td}>{veh.modele || "—"}</td>
		</tr>
	)
}

// Carte "Véhicules" de la fiche client. `onClientUpdate(client)` reçoit le
// client à jour après une save/suppression de véhicule.
const ClientVehiculesV2 = ({
	entiteId,
	vehicules = [],
	onClientUpdate,
	onVehiculeDocsChange = () => {},
	isCreate = false,
}) => {
	const [editing, setEditing] = useState(null) // veh | {} | null

	return (
		<div style={gs.card}>
			<div style={gs.cardTitle}>Véhicules</div>

			{isCreate ? (
				<div style={styles.notice}>
					Enregistrez le client pour pouvoir ajouter des véhicules.
				</div>
			) : (
				<>
					{vehicules.length === 0 ? (
						<div style={styles.empty}>Aucun véhicule.</div>
					) : (
						<table style={tableStyles.table}>
					<thead>
						<tr>
							<th style={styles.th}>Plaques</th>
							<th style={styles.th}>Marque</th>
							<th style={styles.th}>Modèle</th>
						</tr>
					</thead>
					<tbody>
						{vehicules.map((veh, i) => (
							<VehiculeRow
								key={veh.id ?? i}
								veh={veh}
								index={i}
								onOpen={() => setEditing(veh)}
							/>
						))}
					</tbody>
						</table>
					)}
					<button
						type="button"
						style={{ ...gs.addBtn, marginTop: 14 }}
						onClick={() => setEditing({})}
					>
						<FontAwesomeIcon icon={faPlus} />
						Ajouter un véhicule
					</button>
				</>
			)}

			{editing && (
				<VehiculeModal
					vehicule={editing}
					entiteId={entiteId}
					onClose={() => setEditing(null)}
					onSaved={(client) => {
						onClientUpdate(client)
						setEditing(null)
					}}
					onDocsChange={(docs) =>
						onVehiculeDocsChange(editing.id, docs)
					}
				/>
			)}
		</div>
	)
}

export default ClientVehiculesV2
